import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Phone, Menu, X, MapPin, Clock } from "lucide-react";
import { COMPANY } from "@/lib/constants";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/service-areas", label: "Service Areas" },
  { to: "/about", label: "About Us" },
  { to: "/careers", label: "Careers" },
  { to: "/resources", label: "Resources" },
  { to: "/payments", label: "Payments" },
  { to: "/contact", label: "Contact" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);

  return (
    <header data-testid="site-header" className="sticky top-0 z-50">
      {/* Top utility bar */}
      <div className="hidden lg:block bg-primary text-primary-foreground">
        <div className="container-page flex items-center justify-between h-10 text-sm">
          <span className="inline-flex items-center gap-2">
            <MapPin className="w-4 h-4 text-accent" /> Serving Northern Virginia · Annandale, VA
          </span>
          <div className="flex items-center gap-6">
            <span className="inline-flex items-center gap-2 text-primary-foreground/80">
              <Clock className="w-4 h-4" /> Mon–Fri 8:30am–5:30pm · 24/7 On-Call
            </span>
            <a href={COMPANY.phoneHref} className="inline-flex items-center gap-1.5 font-semibold hover:text-accent transition-colors">
              <Phone className="w-4 h-4" /> {COMPANY.phone}
            </a>
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className="bg-background/95 backdrop-blur-md border-b border-border">
        <div className="container-page flex items-center justify-between h-20 gap-4">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3 shrink-0">
            <img src="/esprit-logo.png" alt="Esprit Home Healthcare" className="h-11 lg:h-12 w-auto" />
            <span className="hidden sm:block text-[11px] tracking-[0.22em] uppercase text-muted-foreground font-semibold border-l border-border pl-3 leading-tight">
              Home<br />Healthcare
            </span>
          </Link>

          <nav className="hidden xl:flex items-center gap-0.5">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-[15px] font-medium transition-colors ${
                    isActive ? "text-primary bg-muted" : "text-foreground/80 hover:text-primary hover:bg-muted"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/contact"
              data-testid="header-consult-btn"
              className="hidden lg:inline-flex items-center gap-2 rounded-full bg-accent hover:bg-accent/90 text-accent-foreground px-6 h-12 font-semibold whitespace-nowrap transition-colors"
            >
              Free Consultation
            </Link>
            <a
              href={COMPANY.phoneHref}
              data-testid="header-call-btn"
              className="lg:hidden inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 h-11 font-semibold"
              aria-label="Call us"
            >
              <Phone className="w-5 h-5" />
            </a>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((o) => !o)}
              className="xl:hidden grid place-items-center w-12 h-12 rounded-xl border border-border"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="xl:hidden border-b border-border bg-background">
          <nav className="container-page py-4 flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-lg text-lg font-medium ${isActive ? "text-primary bg-muted" : "text-foreground/80"}`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground px-5 h-14 text-lg font-semibold"
            >
              Free Consultation
            </Link>
            <a
              href={COMPANY.phoneHref}
              className="mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-5 h-14 text-lg font-semibold"
            >
              <Phone className="w-5 h-5" /> Call {COMPANY.phone}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};
